import type { LoiMoiCongKhai } from '@qlk/shared';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { api, type LoiApi } from '../../../lib/api.js';
import { Avatar, EmptyState } from '../../../ui/index.js';

/**
 * Trang công khai `/ket-noi/:token` — chủ kênh tự cấp quyền YouTube.
 *
 * **Không** bọc `CanDangNhap`: người mở link không có tài khoản trong app (xem
 * cờ `congKhai` trong `module.ts`). Vì vậy trang này không gọi `/core/me`,
 * không dựng khung app, và chỉ hỏi đúng một endpoint công khai.
 *
 * Token sai, hết hạn, đã dùng hay đã thu hồi đều ra **một** thông báo như nhau:
 * nói rõ hơn là chỉ điểm cho người dò token.
 */
export function TrangKetNoiCongKhai() {
  const { token = '' } = useParams<{ token: string }>();
  const [thamSo] = useSearchParams();
  const ketQua = thamSo.get('kq');

  const du = useQuery<LoiMoiCongKhai, LoiApi>({
    queryKey: ['sync', 'loi-moi-cong-khai', token],
    queryFn: () => api.get<LoiMoiCongKhai>(`/api/v1/sync/cong-khai/loi-moi/${token}`),
    retry: false,
    // Hỏi lại khi quay về từ Google là vô nghĩa: lời mời lúc đó đã `used`.
    enabled: ketQua === null,
  });

  return (
    <main className="mx-auto flex min-h-dvh max-w-[560px] flex-col justify-center p-6">
      <div className="rounded-the border border-vien bg-be-mat p-6">
        <p className="text-phu font-semibold uppercase tracking-wide text-chu-mo">
          WM Channel Manager
        </p>

        {ketQua === 'ok' ? (
          <Khung
            tieuDe="Đã kết nối xong. Cảm ơn bạn!"
            moTa="Quyền đã được cấp cho WM Media. Bạn có thể đóng trang này — không cần làm gì thêm. Muốn rút lại quyền thì vào myaccount.google.com/permissions."
          />
        ) : ketQua === 'loi' ? (
          <Khung
            loi
            tieuDe="Chưa kết nối được"
            moTa={thamSo.get('lyDo') ?? 'Thử lại từ link mời, hoặc hỏi người đã gửi link.'}
          />
        ) : du.isPending ? (
          <p className="mt-4 text-co-ban text-chu-phu">Đang kiểm tra link…</p>
        ) : du.error ? (
          <KhungLinkSai />
        ) : (
          <>
            <h1 className="mt-2 text-tieu-de-trang font-bold leading-tight text-chu">
              Cấp quyền cho kênh của bạn
            </h1>

            <div className="mt-5 flex items-center gap-3 rounded-[8px] bg-neutral-nen px-3 py-3">
              <Avatar name={du.data.channelName} color={du.data.channelAvatarColor} />
              <div className="min-w-0">
                <p className="truncate text-tieu-de-khoi font-semibold text-chu">
                  {du.data.channelName}
                </p>
                <p className="truncate text-phu text-chu-mo">{du.data.channelHandle}</p>
              </div>
            </div>

            <h2 className="mt-6 text-tieu-de-khoi font-semibold text-chu">WM Media sẽ được phép</h2>
            <ul className="mt-2 flex flex-col gap-2">
              {du.data.quyen.map((q) => (
                <li key={q.key} className="text-co-ban text-chu">
                  <span className="font-semibold">{q.label}</span>
                  <span className="block text-phu text-chu-phu">{q.moTa}</span>
                </li>
              ))}
            </ul>

            <p className="mt-4 text-phu text-chu-mo">
              Đăng nhập bằng đúng tài khoản Google đang quản lý kênh trên. Tài khoản khác sẽ bị từ
              chối. App không thấy mật khẩu của bạn.
            </p>

            <div className="mt-5">
              {/*
               * Thẻ `a` chứ không phải `fetch`: máy chủ trả 302 sang màn hình
               * đồng ý của Google, nên để trình duyệt đi theo chuyển hướng.
               */}
              <a
                href={`/api/v1/sync/cong-khai/loi-moi/${token}/bat-dau`}
                className="vung-bam inline-flex items-center justify-center rounded-[10px] bg-nhan px-4 text-co-ban font-semibold text-white hover:opacity-90"
              >
                Kết nối bằng Google
              </a>
            </div>
          </>
        )}
      </div>
    </main>
  );
}

/**
 * Khối "link không dùng được".
 *
 * Tách ra để test kiểm được đúng câu chữ: test của `apps/web` chạy ở môi trường
 * `node` và dựng cây bằng `react-dom/server`, mà ở đó react-query coi mình đang
 * chạy trên server nên không phơi trạng thái lỗi nạp sẵn trong cache — dựng
 * component này trực tiếp là cách duy nhất kiểm được nhánh đó.
 *
 * Một câu duy nhất cho **mọi** ca (token sai, hết hạn, đã dùng, đã thu hồi):
 * nói rõ hơn là chỉ điểm cho người dò token.
 */
export function KhungLinkSai() {
  return (
    <Khung
      loi
      tieuDe="Link không dùng được"
      moTa="Link có thể đã hết hạn, đã dùng hoặc bị thu hồi. Hỏi người đã gửi link để lấy link mới."
    />
  );
}

function Khung({ tieuDe, moTa, loi = false }: { tieuDe: string; moTa: string; loi?: boolean }) {
  return (
    <div className="mt-4">
      <EmptyState loi={loi} title={tieuDe} description={moTa} />
    </div>
  );
}
